import { useLoadingOverlay } from '@/components'
import { EditablePackageItemTinySelect } from '@/components/EditablePackageItemTinySelect'
import { EditablePriceCustom } from '@/components/EditablePriceCustom'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { usePrintContext } from '@/context/PrintContext'
import { useOffersContext } from '@/context/useOffersContext'
import { useGetRiskVariants } from '@/hooks'
import { useResolveChangedPart } from '@/hooks/useOfferPackage'
import { DismissCircle, Info } from '@/icons'
import { Box, Checkbox, Flex, Icon, Text } from '@chakra-ui/react'
import { faCheck, faTimes } from '@fortawesome/pro-light-svg-icons'
import { faCheck as fasCheck } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { priceFormat } from 'BrokerTsLib/src/Utils/formatPrice'
import CustomTooltip from 'BrokerTsLib/src/components/CustomTooltip'
import type { OfferPackage, PackageItemModel, PackageItemPropertyModel, PartPackage } from 'BrokerTsLib/src/dtos'
import { PackageItemLimitType, Risk } from 'BrokerTsLib/src/enums'
import type { IBodyCell } from 'BrokerTsLib/src/interfaces/offers/grid/columnSections'
import _ from 'lodash'
import { OfferCellWrapper } from './OfferCellWrapper'
// import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

export function IsEditableLimit(packageItem: PackageItemModel, offerPart?: PartPackage) {
  return packageItem.limitType == PackageItemLimitType.Limit &&
    (packageItem.addable
      || packageItem.included
      || offerPart?.riskId == Risk.SPOL_RESPONSIBILITY && offerPart.included // v definici nam chybi priznak, ze je riziko editovatelne i kdyz nelze pridat a neni zahrnuto (vyjimka tedy pro spol. odpovednosit)
    ) &&
    packageItem.variants &&
    (packageItem.variants.length > 1 ||
      packageItem.variants.length == 1 &&
      packageItem.variants[0].limitEditable &&
      packageItem.variants[0].minLimit != packageItem.variants[0].maxLimit)
}

export const GetPackageItemLimit = (offerPart: PartPackage, packageItem: PackageItemModel | PackageItemPropertyModel) => {
  if (offerPart?.limit || offerPart?.limit == 0) {
    let limitText = undefined
    if (offerPart?.packageItemVariantId) {
      if (!packageItem) {
        console.log('packageItem is undefined in GetPackageItemLimit', offerPart)
      }
      limitText = packageItem.variants.find(x => x.id == offerPart.packageItemVariantId)?.limitText
    }
    return [offerPart?.limit, offerPart?.packageItemVariantId, limitText]
  }

  if (!packageItem.variants || packageItem.variants.length == 0)
    return []

  const variant = packageItem.variants.find(x => x.isDefault) ?? packageItem.variants[0]
  return [variant.defaultLimit!, variant.id, variant.limitText]
}

export const changePartLimit = (part: PartPackage, included: boolean, limit?: number, variantId?: number) => {
  if (part.included != included && !part.packageItem.addable) // zahrnuji/odebiram riziko, krete nelze pridat/odebrat
    return false

  if (part.included == included && part.limit == limit && part.packageItemVariantId == variantId)
    return false

  if ((included === undefined || included == false) && part.included == included && limit && part.limit != limit) {
    part.included = true // pokud uživatel změní hodnotu limitu a riziko není ješte v nabídce, má se automaticky přidat - CHALOUPKA-509
  }
  else {
    part.included = included
  }

  if (part.packageItem.limitType == PackageItemLimitType.Limit) {
    part.limit = limit
    part.packageItemVariantId = variantId
  }

  part.changed = true

  return true
}

export function GetPackagePartRenderType(packageItem: PackageItemModel, offerPart?: PartPackage) {
  let childrenType: 'bool' | 'limit' | 'options' | 'text' | undefined
  const isEditableLimit = IsEditableLimit(packageItem, offerPart)

  switch (packageItem.limitType) {
    case PackageItemLimitType.Boolean:
      childrenType = 'bool'
      break

    case PackageItemLimitType.Limit:
      childrenType = isEditableLimit
        ? (packageItem.variants.length > 1 ? 'options' : 'limit')
        : 'text'
      break
  }

  if (!packageItem.included && !packageItem.addable && !offerPart?.included) {
    childrenType = 'bool' // specialita
  }

  return childrenType
}

export const OfferCell = ({
  id,
  offerPart,
  packageItem,
  singleCell = true
}
: IBodyCell
) => {
  const { printMode } = usePrintContext()
  const part = offerPart as PartPackage

  const { updateOffer, offers } = useOffersContext()
  const { isDisabled: ipIsDisabled } = useInsuranceParams()
  const isDisabled = ipIsDisabled || printMode

  const { setLoading } = useLoadingOverlay()
  const { mutateAsync: resolvePart } = useResolveChangedPart()
  const { data: dataPropertyType } = useGetRiskVariants(Risk.PROPERTY_TYPE)

  const propertyTypes = dataPropertyType?.data ?? []

  const o = offers.find(x => x.offerId == part.offerId)! as OfferPackage

  const [value, packageItemVariantId, valueAsText] = GetPackageItemLimit(part, packageItem)

  const pipm = packageItem as PackageItemPropertyModel
  const tooltipValue = !singleCell && pipm?.productTypeRiskVariantId
    ? (
      <Flex flexDirection='column' gap={4}>
        <Text fontWeight='bold'>{propertyTypes.find(x => x.id == pipm.productTypeRiskVariantId)?.name}</Text>
        <Text>{packageItem.description}</Text>
      </Flex>
    )
    : packageItem.description

  const valueText = valueAsText ? valueAsText as string :
    value == 999999999 ? 'Neomezeně' :
      !!value || value == 0 ? priceFormat(value, null, '0 Kč') : undefined

  const included = !!part?.included

  const changeLimit = (included: boolean, limit?: number, variantId?: number) => {
    const offer = _.cloneDeep(o)

    // pokud je partId == 0, tak se jedna o novy part, ktery jeste neni v DB, proto hledame podle packageItemId
    const clonedPart = offer.parts.find(op => op.partId == part.partId && op.partId != 0 ||
      op.partId == 0 && op.packageItem.packageItemId && op.packageItem.packageItemId == packageItem.packageItemId
    ) ?? _.cloneDeep(part)

    changePartLimit(clonedPart, included, limit, variantId)
    if (!clonedPart.changed)
      return

    setLoading(true)

    resolvePart({ offer: offer, packageItemId: packageItem.packageItemId! })
      .then(({ data: updatedOffer }) => {
        updateOffer(updatedOffer) // useEffect na changed zajisti zavolani saveOfferParts a nasledne rekalkulaci
      })
      .finally(() =>
        setTimeout(() => setLoading(false), 100)) // aby loading zmizel az po renderu 
  }

  const childrenType = GetPackagePartRenderType(packageItem, offerPart)

  let children
  switch (childrenType) {
    case 'bool':
      if (included) {
        children = <Icon as={FontAwesomeIcon} icon={fasCheck} color="green.500" mr={isDisabled ? 0 : 5} />
      } else {
        children = packageItem.addable
          ? <Icon as={FontAwesomeIcon} icon={faCheck} color="gray.500" mr={isDisabled ? 0 : 5} />
          : <Icon as={FontAwesomeIcon} icon={faTimes} color="red.500" mr={isDisabled ? 0 : 5} />
      }
      break
    case 'limit':
      {
        const variant = packageItem.variants[0]
        children = (
          <EditablePriceCustom
            value={value}
            text={valueText}
            isDisabled={isDisabled}
            min={variant.minLimit}
            max={variant.maxLimit}
            onSubmit={(limit) => {
              changeLimit(part.included, limit, variant.id)
            }}
          />
        )
      }
      break
    case 'options':
      children = <EditablePackageItemTinySelect
        isDisabled={isDisabled}
        part={part}
        packageItem={packageItem}
        valueText={valueText}
        packageItemVariantId={packageItemVariantId as number}
        onSubmit={(limit, variantId) => changeLimit(part.included, limit, variantId)}
      />
      break
    case 'text':
    default:
      children = valueText
        ? <Text sx={{
          p: 0,
          fontSize: 'sm',
          mr: isDisabled ? 0 : 5
        }}        >
          {valueText}
        </Text>
        : <DismissCircle color="red.500" />
      break
  }


  return (
    <OfferCellWrapper
      key={id}
      sx={{
        gridTemplateColumns: ' 20px auto',
        minW: '140px',
      }}
      riskId={part.riskId}
    >
      {(packageItem.addable && !isDisabled)
        ? (<Checkbox
          size="sm"
          isChecked={included}
          onChange={(e => changeLimit(e.target.checked, value as number, packageItemVariantId as number))}
        />)
        : <Box></Box>
      }

      <Flex justifyContent='flex-end' alignItems='center' gap={2}>
        {offerPart.limitWarning &&
          <CustomTooltip label={offerPart.limitWarning} hasArrow placement="auto">
            <Flex alignItems='center'>
              <Info width={4} height={4} color='yellow.500' transform="rotate(180deg)" />
            </Flex>
          </CustomTooltip>
        }

        <CustomTooltip label={tooltipValue} hasArrow placement="auto">
          <Flex sx={{
            opacity: included ? 1 : 0.5,
            minH: '20px',
            cursor: tooltipValue ? 'help' : 'default',
          }}>
            {children}
          </Flex>
        </CustomTooltip>
      </Flex>


    </OfferCellWrapper>
  )
}